import { apiErrorField, apiErrorMessage } from '@agos/api-client'

import { tt } from './i18n'
import { formatCents, lineTotalCents, parseMoneyToCents, type MoneyInput } from './money'
import { cardPayOnlineLabel, qrPayLabel } from './qr-pay'

/**
 * The virtual receipt — what gets printed when the register is in virtual mode.
 *
 * §11.4 / §14.2: a virtual receipt has no UID because it never reached the tax
 * authority's device. It is **not a fiscal document**, and the footer has to say
 * so, because a customer handed one has no way to tell otherwise.
 *
 * Ported from legacy `pages/restaurant.tsx`'s `printVirtualReceipt`, which
 * `dispatch.tsx` and `fiskal-receipts.tsx` also want. It lives in
 * `packages/shared` for that reason: three modules need it and §5 forbids one
 * importing another, so a module was the wrong home — the boundary lint said so
 * before the first commit.
 *
 * Two things it does differently, both on the tax numbers. See FB-063 and FB-064.
 */

export interface ReceiptLine {
  name?: string
  productName?: string
  quantity?: number
  unitPrice?: MoneyInput
  totalPrice?: MoneyInput
}

export interface VirtualReceipt {
  orderNumber?: string
  issuedAt?: string
  paymentMethod?: string
  total?: MoneyInput
  /** The server's numbers. Preferred over anything computed here. */
  vatRate?: MoneyInput
  vatBase?: MoneyInput
  vatAmount?: MoneyInput
  restaurantName?: string
  restaurantAddress?: string
  restaurantIco?: string
  restaurantDic?: string
  items?: ReceiptLine[]
}

const PAYMENT_LABELS: Record<string, string> = {
  cash: tt('Hotovosť'),
  card_terminal: tt('Karta – terminál'),
  card_courier: tt('Karta – kuriér'),
  // §9.767, owner O129 - the online card's one name; it said *CardPay Online* here.
  card_online: cardPayOnlineLabel(),
  // §9.741 - a door QR payment, filed as `qr_pay` by §10A.390. Without it the virtual receipt said
  // *Karta*, and the change history printed the key: *Hotovosť -> qr_pay*.
  qr_pay: qrPayLabel(),
}

export function paymentLabel(method: string | undefined): string {
  return PAYMENT_LABELS[method ?? ''] ?? tt('Karta')
}

/**
 * Whether `paymentLabel` actually has a word for this slug - `FRONTEND.md` §9.446, FB-763.
 *
 * **The fallback above is right on a receipt and wrong in an audit trail.** A receipt printed for a
 * customer may as well say *Karta* for anything card-shaped; the change history exists so somebody
 * can see what a member of staff switched a payment to, and `card_online → bank_transfer` reading
 * *CardPay Online → Karta* would hide exactly the thing the owner asked to be able to see.
 *
 * Measured on their data: the four slugs in the map are the only four that exist (`cash` 5 308,
 * `card_courier` 2 695, `card_terminal` 1 478, `card_online` 1 306), so this is unreachable today.
 * That is the argument for writing it now rather than after a fifth method is added.
 *
 * **The fifth arrived with §10A.390**: `qr_pay`, which nothing had ever written before, so the map
 * names it in the same change rather than after the history has printed it raw once (§9.741).
 */
export function isKnownPaymentMethod(method: string | undefined): boolean {
  return method !== undefined && method !== '' && method in PAYMENT_LABELS
}

/**
 * HTML escaping, because the receipt is assembled as a string and product names
 * come from the menu editor.
 *
 * FB-063: legacy interpolates `${i.name}` raw. A product called *Coca-Cola 0,5l &
 * hranolky* prints a broken entity, and one containing a `<` swallows the rest of
 * the row. These are staff-entered rather than customer-entered, so it is a
 * correctness problem on a printed document rather than a security hole — but the
 * document is what a customer is handed.
 */
export function escapeHtml(value: string | number | null | undefined): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

export interface ReceiptTax {
  ratePercent: number
  baseCents: number
  vatCents: number
  /** True when the frontend had to work it out itself. */
  derived: boolean
}

/** Slovakia's rates are 5, 19 and 23. 20 has not existed since 2011. */
const NO_RATE = 0

/**
 * The tax block.
 *
 * FB-064: legacy computes this client-side from the gross total with
 * `vatRate ?? 20` — a rate Slovakia retired in 2011, and one the §10A DPH upgrade
 * replaced with per-category 5 / 19 / 23. The endpoint already sends `vatRate`,
 * `vatBase` and `vatAmount` computed server-side and reconciling to the cent
 * (10,15 € = 8,53 + 1,62 at 19% in the dumps), so those are used.
 *
 * The reverse-charge fallback stays for a payload that predates those fields, but
 * it never invents a rate: with no rate there is no VAT line rather than a wrong
 * one.
 */
export function receiptTax(receipt: VirtualReceipt): ReceiptTax {
  const totalCents = parseMoneyToCents(receipt.total)
  const ratePercent = Number(receipt.vatRate ?? NO_RATE)

  const serverBase = receipt.vatBase === undefined ? null : parseMoneyToCents(receipt.vatBase)
  const serverVat = receipt.vatAmount === undefined ? null : parseMoneyToCents(receipt.vatAmount)
  if (serverBase !== null && serverVat !== null) {
    return { ratePercent, baseCents: serverBase, vatCents: serverVat, derived: false }
  }

  if (!Number.isFinite(ratePercent) || ratePercent <= 0) {
    return { ratePercent: 0, baseCents: totalCents, vatCents: 0, derived: true }
  }

  // Gross → net. The total is what was charged, so the base is derived from it
  // rather than the other way round.
  const base = Math.round(totalCents / (1 + ratePercent / 100))
  return { ratePercent, baseCents: base, vatCents: totalCents - base, derived: true }
}

export function lineName(line: ReceiptLine): string {
  return line.name ?? line.productName ?? tt('Položka')
}

export function lineTotal(line: ReceiptLine): number {
  if (line.totalPrice !== undefined) return parseMoneyToCents(line.totalPrice)
  return lineTotalCents(line.unitPrice, line.quantity ?? 0)
}

const DATE_FORMAT = new Intl.DateTimeFormat('sk-SK', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/**
 * The receipt as printable HTML.
 *
 * A string rather than a component because it is printed in its own window with
 * its own stylesheet, at 80 mm — the app's styles have nothing to say about a
 * thermal printer.
 */
export function renderVirtualReceipt(receipt: VirtualReceipt, now: number): string {
  const tax = receiptTax(receipt)
  const totalCents = parseMoneyToCents(receipt.total)
  const issued = DATE_FORMAT.format(new Date(receipt.issuedAt ?? now))

  const rows = (receipt.items ?? [])
    .map(
      (line) =>
        `<tr><td style="width:50%">${escapeHtml(lineName(line))}</td>` +
        `<td style="text-align:center">${escapeHtml(line.quantity ?? 0)}×</td>` +
        `<td style="text-align:right">${escapeHtml(formatCents(lineTotal(line)))}</td></tr>`,
    )
    .join('')

  const vatRows =
    tax.ratePercent > 0
      ? `<tr class="small"><td>${escapeHtml(tt('Základ DPH'))} (${tax.ratePercent}%)</td>` +
        `<td style="text-align:right">${escapeHtml(formatCents(tax.baseCents))}</td></tr>` +
        `<tr class="small"><td>DPH (${tax.ratePercent}%)</td>` +
        `<td style="text-align:right">${escapeHtml(formatCents(tax.vatCents))}</td></tr>`
      : ''

  return `<!DOCTYPE html><html lang="sk"><head><meta charset="utf-8"><title>${escapeHtml(tt('Doklad'))}</title>
<style>
  body{font-family:monospace;font-size:12px;padding:12px;max-width:340px;margin:0 auto}
  h2{font-size:15px;text-align:center;margin:0 0 2px}
  .center{text-align:center} .small{font-size:11px;color:#555}
  table{width:100%;border-collapse:collapse}td{padding:2px 3px}
  hr{border:none;border-top:1px dashed #999;margin:8px 0}
  .total-row{font-size:14px;font-weight:bold}
  .footer{font-size:10px;color:#777;text-align:center;margin-top:10px}
  @media print{@page{margin:4mm}}
</style></head><body>
${
  /* §9.700 - no heading rather than the owner's brand: the server names the franchise on a virtual receipt
     (§10A.329 refuses to print one without), so an absent name here is an old payload, not another restaurant. */
  receipt.restaurantName?.trim() ? `<h2>${escapeHtml(receipt.restaurantName.trim())}</h2>` : ''
}
${receipt.restaurantAddress ? `<p class="center small">${escapeHtml(receipt.restaurantAddress)}</p>` : ''}
${
  receipt.restaurantIco
    ? `<p class="center small">${escapeHtml(tt('IČO:'))} ${escapeHtml(receipt.restaurantIco)}${
        receipt.restaurantDic
          ? ` | ${escapeHtml(tt('DIČ:'))} ${escapeHtml(receipt.restaurantDic)}`
          : ''
      }</p>`
    : ''
}
<hr>
<p class="small center">${escapeHtml(tt('Doklad č.:'))} ${escapeHtml(receipt.orderNumber ?? '')} &nbsp;|&nbsp; ${escapeHtml(issued)}</p>
<hr>
<table><thead><tr><th style="text-align:left">${escapeHtml(tt('Položka'))}</th><th>${escapeHtml(tt('Mn.'))}</th><th style="text-align:right">${escapeHtml(tt('Cena'))}</th></tr></thead>
<tbody>${rows}</tbody></table>
<hr>
<table>
  ${vatRows}
  <tr class="total-row"><td>${escapeHtml(tt('CELKOM'))}</td><td style="text-align:right">${escapeHtml(formatCents(totalCents))}</td></tr>
</table>
<hr>
<p class="small center">${escapeHtml(tt('Platba:'))} ${escapeHtml(paymentLabel(receipt.paymentMethod))}</p>
<p class="footer">${escapeHtml(tt('Virtuálna kasa - doklad nie je daňovým dokladom ERP'))}</p>
</body></html>`
}

/**
 * Opens the receipt in its own window and prints it.
 *
 * The print script is attached as a handler rather than written into the markup:
 * a `<script>` tag inside a `document.write` string is what makes the whole thing
 * unescapable, and the caller wants the print either way.
 */
export function printVirtualReceipt(receipt: VirtualReceipt, now: number): boolean {
  const target = window.open('', '_blank', 'width=380,height=700')
  if (!target) return false

  target.document.write(renderVirtualReceipt(receipt, now))
  target.document.close()
  target.onload = () => {
    target.print()
    setTimeout(() => target.close(), 600)
  }
  return true
}

/**
 * The receipt the **browser** must print, or `null` when the register printed it - §9.498.
 *
 * `POST /fiskal/receipt` does not always reach the register. `FiskalReceiptService` flags a receipt
 * printed while the register was unreachable as virtual - its `Printed` record carries
 * `circuitBreakerFallback`, *"true when this is virtual because the register could not be reached"* -
 * and the controller then answers **200** with `virtualReceipt: true` and the receipt in the body.
 * Nothing came out of the register, so a caller that ignores the body has printed nothing at all.
 *
 * **That is what the dispatch board did.** Its batch print saw a resolved promise, counted the order
 * among the issued, printed the delivery slip and threw the receipt away - so an unreachable register
 * produced a slip and no document, which is the owner's report of 2026-09-26.
 *
 * **A reader rather than a printer, deliberately.** `printVirtualReceipt` opens a window and the toast
 * lives in its own module, so a shared function doing all three would drag both into
 * `packages/shared`. This is the split {@link alreadyPrinted} and {@link alreadyPrintedNote} already
 * use: the shared half decides, the caller says what the operator sees. It is also what makes the rule
 * testable, which two hand-written copies of it were not.
 *
 * **Both halves are required**, because either alone is a receipt nobody can print: the flag without a
 * body is nothing to render, and a body without the flag is the register's own answer, which is an
 * outcome record rather than a receipt.
 */
export function virtualReceiptOf(answer: unknown): VirtualReceipt | null {
  if (answer === null || typeof answer !== 'object') return null
  const body = answer as { virtualReceipt?: unknown; receipt?: unknown }
  if (body.virtualReceipt !== true) return null
  const receipt = body.receipt
  // Narrowed rather than asserted: every field on `VirtualReceipt` is optional, so `object` already
  // satisfies it and lint refuses the cast. The narrowing is the check, which is the point.
  if (receipt === null || typeof receipt !== 'object') return null
  return receipt
}

/**
 * Warnings the register raises that a waiter must not be interrupted by - §9.500.
 *
 * <p>States 40, 41, 50 and 56 mean the till has lost its connection to the tax authority's server. The
 * receipt is still valid and the till reconnects and uploads by itself. Toasting these mid-service trains
 * staff to dismiss register warnings without reading them - which is exactly what must not happen when the
 * certificate or the CHDU is the one complaining.
 *
 * <p><b>Moved here from `manager-orders/restaurant/split-payment.ts` by §9.500</b>, unchanged, because the
 * dispatch board needs the same rule and §5 forbids one module importing another. That file re-exports it,
 * so its nine existing assertions and `PaymentDialog`'s import are untouched - a moved function whose tests
 * were rewritten beside it would say nothing about the move.
 */
export function isTransientKasaWarning(warning: unknown): boolean {
  const text = typeof warning === 'string' ? warning : JSON.stringify(warning ?? '')
  return (
    text.includes('offline/pending')
    || text.includes('EKASA_OFFLINE_STATE50')
    || /stav (40|41|50|56)/.test(text)
  )
}

/** What a print answered about the register itself - §9.500. */
export interface RegisterWarnings {
  /** The warning worth showing an operator, or `null` for none and for the reconnecting ones. */
  warning: string | null
  /** The register closed the document and has no paper, so it prints when paper is inserted. */
  paperOut: boolean
}

/**
 * The register's own report on a print, read off the answer - §9.500.
 *
 * <p><b>Both fields were thrown away by the dispatch board.</b> `PaymentDialog` has read them since §9.367;
 * the board awaited the same call and used nothing from it, so a batch could report <em>Vytlacene fiskalne
 * doklady: 8</em> with nothing on the tray, because {@code paperOut} after a successful close is an
 * <b>ok</b> outcome by design - the document is registered and reprints when paper is inserted.
 *
 * <p><b>A reader rather than a toaster</b>, for the reason {@link virtualReceiptOf} gives: `toast` is its
 * own module and a shared function that raised it would drag that into `packages/shared`. It decides; the
 * caller words it.
 *
 * <p>The transient states are filtered here rather than at the call site, so a second caller cannot forget
 * to - which is what this entry is about.
 */
export function registerWarningsOf(answer: unknown): RegisterWarnings {
  if (answer === null || typeof answer !== 'object') return { warning: null, paperOut: false }
  const body = answer as { warning?: unknown; paperOut?: unknown }
  const raw = body.warning
  const worthShowing =
    raw === null || raw === undefined || raw === '' || isTransientKasaWarning(raw)
      ? null
      : typeof raw === 'string'
        ? raw
        : JSON.stringify(raw)
  return { warning: worthShowing, paperOut: body.paperOut === true }
}

/**
 * Whether a refused print means **the receipt already exists** - §9.362, FB-649.
 *
 * `FiskalReceiptService` answers `409` with code `ALREADY_PRINTED` and a sentence that carries the
 * document's number: *"Fiskálny doklad pre túto objednávku bol už vytlačený (č. X). Ak potrebujete
 * nový doklad, najprv stornujte pôvodný."* **That is a statement about state, not a failure to
 * print** - and two screens were reporting it as one: the till as a destructive *"Pokladňa - chyba"*,
 * and the dispatch board by counting the order among the failures, which sends somebody to chase a
 * receipt that is already on the tray.
 *
 * **Here rather than in either module** for the reason this file's index entry already gives: the
 * restaurant till, the dispatch board and the fiscal-receipts page all print, and §5 forbids a module
 * owning what all three need. The three callers of `POST /fiskal/receipt` are those same three.
 *
 * **Read through `apiErrorField`**, which is the client's own envelope reader. Spelling
 * `error.data.code` here by hand is what FB-645 was: nine handlers reading a property `ApiError`
 * does not have, each of them plausible on its own.
 *
 * **Why an operator meets this at all**, beyond a genuine duplicate: a first attempt whose answer
 * was lost. The owner's 2026-09-18 incident is the shape - the kasa prints, the connection is cut
 * before the answer arrives, the retry is refused as a duplicate, and the only thing on screen is
 * whatever this decides to say.
 */
export function alreadyPrinted(error: unknown): boolean {
  return apiErrorField(error, 'code') === ALREADY_PRINTED
}

/** The backend's own code, named once. */
const ALREADY_PRINTED = 'ALREADY_PRINTED'

/**
 * What to tell the operator when a print was refused because the document exists.
 *
 * The server's sentence when there is one - it names the receipt number, which nothing on this side
 * knows - and a fallback that says the same thing without inventing one.
 */
export function alreadyPrintedNote(error: unknown): string {
  return apiErrorMessage(error, tt('Fiskálny doklad pre túto objednávku už bol vytlačený.'))
}
